import React, { useState, useEffect } from 'react';
import { issueService } from '../services/issueService';
import type { Issue, IssueComment } from '../types/issue';
import './IssueTracker.css';

interface IssueTrackerProps {
  onNavigate: (page: string) => void;
}

const ISSUES_PER_PAGE = 10;

const IssueTracker: React.FC<IssueTrackerProps> = ({ onNavigate }) => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState<'recent' | 'top'>('recent');
  const [commentCounts, setCommentCounts] = useState<Map<string, number>>(new Map());
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [comments, setComments] = useState<IssueComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentsPage, setCommentsPage] = useState(0);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);
  const [totalComments, setTotalComments] = useState(0);
  const [isSelectedIssueOwner, setIsSelectedIssueOwner] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [userComments, setUserComments] = useState<Set<string>>(new Set());
  const [userIssues, setUserIssues] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'maintenance',
    location: '',
    photo: null as File | null
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [userVotes, setUserVotes] = useState<Map<string, 'upvote' | 'downvote'>>(new Map());
  const [userCommentVotes, setUserCommentVotes] = useState<Map<string, 'upvote' | 'downvote'>>(new Map());
  const [votingInProgress, setVotingInProgress] = useState<Set<string>>(new Set());
  const [commentVotingInProgress, setCommentVotingInProgress] = useState<Set<string>>(new Set());
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ show: false, title: '', message: '', onConfirm: () => {} });

  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmModal({ show: true, title, message, onConfirm });
  };

  const closeConfirmDialog = () => {
    setConfirmModal({ show: false, title: '', message: '', onConfirm: () => {} });
  };

  const categories = [
    'maintenance',
    'cleanliness', 
    'safety',
    'facilities',
    'technology',
    'other'
  ];

  const statusColors = {
    'reported': '#f39c12',
    'in_progress': '#3498db', 
    'fixed': '#27ae60',
    'rejected': '#e74c3c'
  };

  useEffect(() => {
    loadIssues();
  }, []);

  const loadIssues = async (reset: boolean = true, tab: 'recent' | 'top' = activeTab) => {
    try {
      const page = reset ? 0 : currentPage;
      
      // Use different API based on tab
      const { issues: data, hasMore: more } = tab === 'top' 
        ? await issueService.getTopIssuesPaginated(page, ISSUES_PER_PAGE)
        : await issueService.getIssuesPaginated(page, ISSUES_PER_PAGE);
      
      if (reset) {
        setIssues(data);
        setCurrentPage(0);
      } else {
        setIssues(prev => [...prev, ...data]);
      }
      setHasMore(more);
      
      // Load user's votes and comment counts for all loaded issues
      const [votes, counts] = await Promise.all([
        issueService.getUserVotesForAllIssues(),
        issueService.getCommentCounts(data.map(i => i.id))
      ]);
      setUserVotes(votes);
      setCommentCounts(prev => {
        const newCounts = new Map(prev);
        counts.forEach((count, id) => newCounts.set(id, count));
        return newCounts;
      });
      
      // Check ownership for loaded issues
      if (reset) {
        const userIssueIds = new Set<string>();
        for (const issue of data) {
          try {
            const isUserIssue = await issueService.isUserIssue(issue.id);
            if (isUserIssue) {
              userIssueIds.add(issue.id);
            }
          } catch (error) {
            console.error('Error checking issue ownership:', error);
          }
        }
        setUserIssues(userIssueIds);
      }
    } catch (error) {
      console.error('Error loading issues:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const switchTab = (tab: 'recent' | 'top') => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setLoading(true);
    setIssues([]);
    loadIssues(true, tab);
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setCurrentPage(prev => prev + 1);
    
    try {
      // Use different API based on active tab
      const { issues: data, hasMore: more } = activeTab === 'top'
        ? await issueService.getTopIssuesPaginated(currentPage + 1, ISSUES_PER_PAGE)
        : await issueService.getIssuesPaginated(currentPage + 1, ISSUES_PER_PAGE);
      
      setIssues(prev => [...prev, ...data]);
      setHasMore(more);
      
      // Fetch comment counts for new issues
      const counts = await issueService.getCommentCounts(data.map(i => i.id));
      setCommentCounts(prev => {
        const newCounts = new Map(prev);
        counts.forEach((count, id) => newCounts.set(id, count));
        return newCounts;
      });
      
      // Check ownership for new issues
      for (const issue of data) {
        try {
          const isUserIssue = await issueService.isUserIssue(issue.id);
          if (isUserIssue) {
            setUserIssues(prev => new Set(prev).add(issue.id));
          }
        } catch (error) {
          console.error('Error checking issue ownership:', error);
        }
      }
    } catch (error) {
      console.error('Error loading more issues:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let photoUrl = '';
      
      if (formData.photo) {
        const uploadedUrl = await issueService.uploadIssuePhoto(formData.photo);
        if (uploadedUrl) {
          photoUrl = uploadedUrl;
        }
      }

      await issueService.createIssue({
        title: formData.title,
        description: formData.description,
        category: formData.category,
        location: formData.location,
        status: 'reported',
        photo_url: photoUrl || undefined
      });

      setFormData({
        title: '',
        description: '',
        category: 'maintenance',
        location: '',
        photo: null
      });
      setPhotoPreview(null);
      setShowForm(false);
      loadIssues();
    } catch (error) {
      console.error('Error creating issue:', error);
      alert('Failed to create issue. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData({ ...formData, photo: file });
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPhotoPreview(null);
    }
  };

  const removePhoto = () => {
    setFormData({ ...formData, photo: null });
    setPhotoPreview(null);
  };

  const handleVote = async (issueId: string, voteType: 'upvote' | 'downvote') => {
    // Prevent double-clicking
    if (votingInProgress.has(issueId)) return;
    
    setVotingInProgress(prev => new Set(prev).add(issueId));
    
    // Optimistic UI update for vote state
    const currentVote = userVotes.get(issueId);
    setUserVotes(prev => {
      const newVotes = new Map(prev);
      if (currentVote === voteType) {
        // Clicking same button removes vote
        newVotes.delete(issueId);
      } else {
        // Set new vote
        newVotes.set(issueId, voteType);
      }
      return newVotes;
    });
    
    try {
      await issueService.voteOnIssue(issueId, voteType);
      
      // Fetch updated data from server
      const [updatedIssues, updatedVotes] = await Promise.all([
        issueService.getIssues(),
        issueService.getUserVotesForAllIssues()
      ]);
      setIssues(updatedIssues);
      setUserVotes(updatedVotes);
    } catch (error) {
      console.error('Error voting:', error);
      // Revert optimistic update on error
      const votes = await issueService.getUserVotesForAllIssues();
      setUserVotes(votes);
    } finally {
      setVotingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(issueId);
        return newSet;
      });
    }
  };

  const openIssueDetails = async (issue: Issue) => {
    setSelectedIssue(issue);
    setComments([]);
    setCommentsPage(0);
    setIsSelectedIssueOwner(false);
    
    try {
      // Load everything in parallel with batch ownership check
      const [commentsResult, commentVotes, userCommentIds, isOwner] = await Promise.all([
        issueService.getIssueCommentsPaginated(issue.id, 0, 10),
        issueService.getUserVotesForComments(issue.id),
        issueService.getUserCommentIds(issue.id),
        issueService.isUserIssue(issue.id)
      ]);
      
      setComments(commentsResult.comments);
      setHasMoreComments(commentsResult.hasMore);
      setTotalComments(commentsResult.total);
      setUserCommentVotes(commentVotes);
      setUserComments(userCommentIds);
      setIsSelectedIssueOwner(isOwner);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleStatusChange = async (newStatus: 'reported' | 'in_progress' | 'fixed' | 'rejected') => {
    if (!selectedIssue || updatingStatus) return;
    
    setUpdatingStatus(true);
    try {
      await issueService.updateIssueStatus(selectedIssue.id, newStatus);
      
      // Update local state
      setSelectedIssue({ ...selectedIssue, status: newStatus });
      setIssues(prev => prev.map(issue => 
        issue.id === selectedIssue.id ? { ...issue, status: newStatus } : issue
      ));
    } catch (error: any) {
      alert(error.message || 'Error updating status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCardStatusChange = async (issueId: string, newStatus: 'reported' | 'in_progress' | 'fixed' | 'rejected') => {
    try {
      await issueService.updateIssueStatus(issueId, newStatus);
      
      // Update local state
      setIssues(prev => prev.map(issue => 
        issue.id === issueId ? { ...issue, status: newStatus } : issue
      ));
    } catch (error: any) {
      alert(error.message || 'Error updating status');
    }
  };

  const loadMoreComments = async () => {
    if (!selectedIssue || loadingMoreComments || !hasMoreComments) return;
    
    setLoadingMoreComments(true);
    const nextPage = commentsPage + 1;
    
    try {
      const { comments: moreComments, hasMore } = await issueService.getIssueCommentsPaginated(
        selectedIssue.id, 
        nextPage, 
        10
      );
      
      setComments(prev => [...prev, ...moreComments]);
      setCommentsPage(nextPage);
      setHasMoreComments(hasMore);
      
      // Check ownership for new comments
      for (const comment of moreComments) {
        const isOwner = await issueService.isUserComment(comment.id);
        if (isOwner) {
          setUserComments(prev => new Set(prev).add(comment.id));
        }
      }
    } catch (error) {
      console.error('Error loading more comments:', error);
    } finally {
      setLoadingMoreComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedIssue) return;

    try {
      const newCommentData = await issueService.addComment(selectedIssue.id, newComment);
      setNewComment('');
      
      // Update comments list
      const updatedComments = await issueService.getIssueComments(selectedIssue.id);
      setComments(updatedComments);
      setTotalComments(prev => prev + 1);
      
      // Update comment count in the cards
      setCommentCounts(prev => {
        const newCounts = new Map(prev);
        newCounts.set(selectedIssue.id, (prev.get(selectedIssue.id) || 0) + 1);
        return newCounts;
      });
      
      // Add new comment to user's owned comments immediately
      setUserComments(prev => new Set(prev).add(newCommentData.id));
    } catch (error: any) {
      alert(error.message || 'Error adding comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selectedIssue) return;
    
    showConfirmDialog(
      'Delete Comment',
      'Are you sure you want to delete this comment?',
      async () => {
        closeConfirmDialog();
        try {
          await issueService.deleteComment(commentId);
          // Remove from local state
          setComments(prev => prev.filter(comment => comment.id !== commentId));
          setTotalComments(prev => Math.max(0, prev - 1));
          setUserComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(commentId);
            return newSet;
          });
          
          // Update comment count in the cards
          setCommentCounts(prev => {
            const newCounts = new Map(prev);
            newCounts.set(selectedIssue.id, Math.max(0, (prev.get(selectedIssue.id) || 0) - 1));
            return newCounts;
          });
        } catch (error: any) {
          alert(error.message || 'Error deleting comment');
        }
      }
    );
  };

  const handleDeleteIssue = async (issueId: string) => {
    showConfirmDialog(
      'Delete Issue',
      'Are you sure you want to delete this issue? This cannot be undone.',
      async () => {
        closeConfirmDialog();
        try {
          setLoading(true);
          await issueService.deleteIssue(issueId);
          // Remove from local state instead of full reload
          setIssues(prev => prev.filter(issue => issue.id !== issueId));
          setUserIssues(prev => {
            const newSet = new Set(prev);
            newSet.delete(issueId);
            return newSet;
          });
        } catch (error: any) {
          alert(error.message || 'Error deleting issue');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleCommentVote = async (commentId: string, voteType: 'upvote' | 'downvote') => {
    if (!selectedIssue) return;
    
    // Prevent double-clicking
    if (commentVotingInProgress.has(commentId)) return;
    
    setCommentVotingInProgress(prev => new Set(prev).add(commentId));
    
    // Optimistic UI update for vote state
    const currentVote = userCommentVotes.get(commentId);
    setUserCommentVotes(prev => {
      const newVotes = new Map(prev);
      if (currentVote === voteType) {
        newVotes.delete(commentId);
      } else {
        newVotes.set(commentId, voteType);
      }
      return newVotes;
    });
    
    try {
      await issueService.voteOnComment(commentId, voteType);
      
      // Fetch updated data from server
      const [updatedComments, updatedVotes] = await Promise.all([
        issueService.getIssueComments(selectedIssue.id),
        issueService.getUserVotesForComments(selectedIssue.id)
      ]);
      setComments(updatedComments);
      setUserCommentVotes(updatedVotes);
    } catch (error) {
      console.error('Error voting on comment:', error);
      // Revert on error
      const votes = await issueService.getUserVotesForComments(selectedIssue.id);
      setUserCommentVotes(votes);
    } finally {
      setCommentVotingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });
    }
  };

  const getStats = () => {
    const total = issues.length;
    const fixed = issues.filter(i => i.status === 'fixed').length;
    const pending = issues.filter(i => i.status === 'reported' || i.status === 'in_progress').length;
    
    return { total, fixed, pending };
  };

  const stats = getStats();

  if (loading && issues.length === 0) {
    return <div className="issue-tracker loading">Loading issues...</div>;
  }

  return (
    <div className="issue-tracker">
      <header className="issue-header">
        <h1>Campus Issue Tracker</h1>
        <button className="back-btn" onClick={() => onNavigate('dashboard')}>
          ← Back
        </button>
      </header>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">Total Issues</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{stats.fixed}</span>
          <span className="stat-label">Fixed</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{stats.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{Math.round((stats.fixed / stats.total) * 100) || 0}%</span>
          <span className="stat-label">Success Rate</span>
        </div>
      </div>

      <div className="issue-actions">
        <button 
          className="submit-issue-btn"
          onClick={() => setShowForm(true)}
        >
          + Report Issue
        </button>
      </div>

      {/* Tabs */}
      <div className="issue-tabs">
        <button 
          className={`tab-btn ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => switchTab('recent')}
        >
          🕐 Recent
        </button>
        <button 
          className={`tab-btn ${activeTab === 'top' ? 'active' : ''}`}
          onClick={() => switchTab('top')}
        >
          ❗ Top Issues
        </button>
      </div>

      {showForm && (
        <div className="issue-form-modal">
          <div className="issue-form">
            <h3>Report New Issue</h3>
            <form onSubmit={handleSubmit}>
              <div className="input-with-counter">
                <input
                  type="text"
                  placeholder="Issue Title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value.slice(0, 100)})}
                  maxLength={100}
                  required
                />
                <span className="char-counter">{formData.title.length}/100</span>
              </div>
              
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>

              <div className="input-with-counter">
                <input
                  type="text"
                  placeholder="Location (e.g., Library 2nd Floor)"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value.slice(0, 100)})}
                  maxLength={100}
                  required
                />
                <span className="char-counter">{formData.location.length}/100</span>
              </div>

              <div className="input-with-counter">
                <textarea
                  placeholder="Describe the issue in detail..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value.slice(0, 500)})}
                  maxLength={500}
                  rows={4}
                  required
                />
                <span className="char-counter">{formData.description.length}/500</span>
              </div>

              <div className="photo-upload-section">
                <label className="photo-upload-label">
                  📷 {formData.photo ? 'Change Photo' : 'Add Photo (Optional)'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                </label>
                
                {photoPreview && (
                  <div className="photo-preview">
                    <img src={photoPreview} alt="Preview" />
                    <button type="button" className="remove-photo-btn" onClick={removePhoto}>
                      ✕ Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="form-buttons">
                <button type="button" onClick={() => { setShowForm(false); setPhotoPreview(null); }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting}>
                  {submitting ? (formData.photo ? 'Uploading Photo...' : 'Submitting...') : 'Submit Issue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="issues-list">
        {issues.map(issue => (
          <div key={issue.id} className="issue-card">
            <div className="issue-header-row">
              <h3 onClick={() => openIssueDetails(issue)}>{issue.title}</h3>
              {userIssues.has(issue.id) ? (
                <select
                  className="status-select-card"
                  value={issue.status}
                  onChange={(e) => handleCardStatusChange(issue.id, e.target.value as any)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="reported">Reported</option>
                  <option value="in_progress">In Progress</option>
                  <option value="fixed">Fixed</option>
                  <option value="rejected">Rejected</option>
                </select>
              ) : (
                <span 
                  className="issue-status"
                  style={{ backgroundColor: statusColors[issue.status] }}
                >
                  {issue.status.replace('_', ' ')}
                </span>
              )}
            </div>
            
            <p className="issue-meta">
              {issue.category} • {issue.location} • {new Date(issue.created_at).toLocaleDateString()}
            </p>
            
            {issue.photo_url && (
              <div className="issue-photo-thumbnail" onClick={() => openIssueDetails(issue)}>
                <img src={issue.photo_url} alt="Issue" />
              </div>
            )}
            
            <p className="issue-description">{issue.description}</p>
            
            <div className="issue-voting">
              <button 
                className={`vote-btn upvote ${userVotes.get(issue.id) === 'upvote' ? 'active' : ''}`}
                onClick={() => handleVote(issue.id, 'upvote')}
                disabled={votingInProgress.has(issue.id)}
              >
                ↑ {issue.upvotes}
              </button>
              <button 
                className={`vote-btn downvote ${userVotes.get(issue.id) === 'downvote' ? 'active' : ''}`}
                onClick={() => handleVote(issue.id, 'downvote')}
                disabled={votingInProgress.has(issue.id)}
              >
                ↓ {issue.downvotes}
              </button>
              <button 
                className="comment-btn"
                onClick={() => openIssueDetails(issue)}
              >
                💬 {commentCounts.get(issue.id) || 0} {(commentCounts.get(issue.id) || 0) === 1 ? 'Comment' : 'Comments'}
              </button>
              {userIssues.has(issue.id) && (
                <button 
                  className="delete-issue-btn"
                  onClick={() => handleDeleteIssue(issue.id)}
                >
                  🗑️ Delete Post
                </button>
              )}
            </div>
          </div>
        ))}
        
        {/* Load More Button */}
        {hasMore && (
          <button 
            className="load-more-btn"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More Issues'}
          </button>
        )}
        
        {!hasMore && issues.length > 0 && (
          <p className="no-more-issues">You've reached the end!</p>
        )}
      </div>

      {selectedIssue && (
        <div className="issue-modal" onClick={() => setSelectedIssue(null)}>
          <div className="issue-details" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedIssue.title}</h2>
            </div>
            
            <div className="issue-info">
              <p><strong>Category:</strong> {selectedIssue.category}</p>
              <p><strong>Location:</strong> {selectedIssue.location}</p>
              <p>
                <strong>Status:</strong>{' '}
                {isSelectedIssueOwner ? (
                  <select
                    className="status-select"
                    value={selectedIssue.status}
                    onChange={(e) => handleStatusChange(e.target.value as any)}
                    disabled={updatingStatus}
                  >
                    <option value="reported">Reported</option>
                    <option value="in_progress">In Progress</option>
                    <option value="fixed">Fixed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                ) : (
                  <span className={`status-badge status-${selectedIssue.status}`}>
                    {selectedIssue.status.replace('_', ' ')}
                  </span>
                )}
                {updatingStatus && <span className="updating-text"> Updating...</span>}
              </p>
              <p><strong>Description:</strong> {selectedIssue.description}</p>
              
              {selectedIssue.photo_url && (
                <div className="issue-photo-full">
                  <img src={selectedIssue.photo_url} alt="Issue" />
                </div>
              )}
            </div>

            <div className="comments-section">
              <h3>Comments ({totalComments})</h3>
              
              <div className="add-comment">
                <div className="input-with-counter">
                  <textarea
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value.slice(0, 300))}
                    maxLength={300}
                    rows={3}
                  />
                  <span className="char-counter">{newComment.length}/300</span>
                </div>
                <button onClick={handleAddComment} disabled={!newComment.trim()}>Post Comment</button>
              </div>

              <div className="comments-list">
                {comments.length === 0 && (
                  <p className="no-comments">No comments yet. Be the first to comment!</p>
                )}
                {comments.map(comment => (
                  <div key={comment.id} className="comment">
                    <p>{comment.content}</p>
                    <div className="comment-meta">
                      <span>{new Date(comment.created_at).toLocaleDateString()}</span>
                      <div className="comment-actions">
                        <div className="comment-voting">
                          <button 
                            className={userCommentVotes.get(comment.id) === 'upvote' ? 'active-upvote' : ''}
                            onClick={() => handleCommentVote(comment.id, 'upvote')}
                            disabled={commentVotingInProgress.has(comment.id)}
                          >
                            ↑ {comment.upvotes}
                          </button>
                          <button 
                            className={userCommentVotes.get(comment.id) === 'downvote' ? 'active-downvote' : ''}
                            onClick={() => handleCommentVote(comment.id, 'downvote')}
                            disabled={commentVotingInProgress.has(comment.id)}
                          >
                            ↓ {comment.downvotes}
                          </button>
                        </div>
                        {userComments.has(comment.id) && (
                          <button 
                            className="delete-comment-btn"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Load More Comments */}
                {hasMoreComments && (
                  <button 
                    className="load-more-comments-btn"
                    onClick={loadMoreComments}
                    disabled={loadingMoreComments}
                  >
                    {loadingMoreComments ? 'Loading...' : `Load More (${comments.length}/${totalComments})`}
                  </button>
                )}
              </div>
              
              {/* Close button at bottom */}
              <button 
                className="close-comments-btn"
                onClick={() => setSelectedIssue(null)}
              >
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="confirm-modal-overlay" onClick={closeConfirmDialog}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmModal.title}</h3>
            <p>{confirmModal.message}</p>
            <div className="confirm-modal-buttons">
              <button className="cancel-btn" onClick={closeConfirmDialog}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={confirmModal.onConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IssueTracker;